/**
 * TodoSection.ts
 *
 * Todo 섹션 분류 및 Tree 데이터 생성 로직
 */

import i18n from 'i18n'
import moment, { Moment } from 'moment'
import { SortType } from './TodoFilter'
import { TodoItem } from './TodoItem'
import { sortTodos } from './TodoSort'

// value 값을 key 로 사용.
export const SectionType = {
  Earlier: 'earlier', // Overdue - 기한이 지난 할일 목록
  Today: 'today', // Due date가 오늘인 할일 목록
  Tomorrow: 'tomorrow', // Due date가 내일인 할일 목록
  ThisWeek: 'thisWeek', // Due date가 이번주인 할일 목록
  NextWeek: 'nextWeek', // Due date가 다음주인 할일 목록
  Later: 'later', // 나중에 (그 이후)
  Active: 'active', // 활성 할일 목록
  Completed: 'completed', // 완료된 할일 목록
  TodayCompleted: 'todayCompleted', // 오늘 완료된 할일 목록
  ActiveCompleted: 'activeCompleted', // 활성 완료된 할일 목록
} as const

// type SectionTypeValue = 'earlier' | 'today' | 'tomorrow' | 'thisWeek' | 'nextWeek' | 'later'
export type SectionTypeValue = (typeof SectionType)[keyof typeof SectionType]

// 날짜 기반 섹션 타입 (categorizeTodosBySection에서 사용)
export type DateBasedSectionType =
  | typeof SectionType.Earlier
  | typeof SectionType.Today
  | typeof SectionType.Tomorrow
  | typeof SectionType.ThisWeek
  | typeof SectionType.NextWeek
  | typeof SectionType.Later

export interface TodoSection {
  key: string
  title: string
  type: SectionTypeValue
  count: number
  todos: TodoItem[]
}

interface WeekBounds {
  thisWeekStart: Moment
  thisWeekEnd: Moment
  nextWeekStart: Moment
  nextWeekEnd: Moment
}

/**
 * 주의 시작일 기준으로 이번주/다음주 계산
 */
const getWeekBounds = (startOfWeek: number = 1): WeekBounds => {
  // 1 = Monday, 0 = Sunday
  const now = moment()
  const thisWeekStart = now
    .clone()
    .startOf('isoWeek')
    .add(startOfWeek === 0 ? -1 : 0, 'days')
  const thisWeekEnd = thisWeekStart.clone().add(6, 'days').endOf('day')
  const nextWeekStart = thisWeekEnd.clone().add(1, 'day').startOf('day')
  const nextWeekEnd = nextWeekStart.clone().add(6, 'days').endOf('day')

  return {
    thisWeekStart,
    thisWeekEnd,
    nextWeekStart,
    nextWeekEnd,
  }
}

/**
 * Todo를 섹션별로 분류
 */
export const categorizeTodosBySection = (
  todos: TodoItem[],
  startOfWeek: number = 1,
  sortType?: SortType,
  locale: string = 'en',
): TodoSection[] => {
  const now = moment()
  const today = now.clone().startOf('day')
  const tomorrow = today.clone().add(1, 'day')
  const { thisWeekStart, thisWeekEnd, nextWeekStart, nextWeekEnd } = getWeekBounds(startOfWeek)

  const sections: Record<DateBasedSectionType, TodoItem[]> = {
    [SectionType.Earlier]: [],
    [SectionType.Today]: [],
    [SectionType.Tomorrow]: [],
    [SectionType.ThisWeek]: [],
    [SectionType.NextWeek]: [],
    [SectionType.Later]: [],
  }

  todos.forEach((todo) => {
    if (!todo.dueDate) {
      // Due date가 없는 경우 section에 포함하지 않음
      //sections[SectionType.Later].push(todo)
    } else {
      const dueDate = moment(todo.dueDate).startOf('day')

      if (dueDate.isBefore(today)) {
        // 기한이 지난 경우
        sections[SectionType.Earlier].push(todo)
      } else if (dueDate.isSame(today, 'day')) {
        // 오늘
        sections[SectionType.Today].push(todo)
      } else if (dueDate.isSame(tomorrow, 'day')) {
        // 내일
        sections[SectionType.Tomorrow].push(todo)
      } else if (dueDate.isBetween(thisWeekStart, thisWeekEnd, 'day', '[]')) {
        // 이번주 (오늘, 내일 제외)
        sections[SectionType.ThisWeek].push(todo)
      } else if (dueDate.isBetween(nextWeekStart, nextWeekEnd, 'day', '[]')) {
        // 다음주
        sections[SectionType.NextWeek].push(todo)
      } else {
        // 그 이후
        sections[SectionType.Later].push(todo)
      }
    }
  })

  // 섹션 정보 생성
  const sectionTitles: Record<DateBasedSectionType, string> = {
    [SectionType.Earlier]: (i18n as any).t('todo.todolist.earlier'),
    [SectionType.Today]: (i18n as any).t('todo.todolist.today'),
    [SectionType.Tomorrow]: (i18n as any).t('todo.todolist.tomorrow'),
    [SectionType.ThisWeek]: (i18n as any).t('todo.todolist.thisWeek'),
    [SectionType.NextWeek]: (i18n as any).t('todo.todolist.nextWeek'),
    [SectionType.Later]: (i18n as any).t('todo.todolist.later'),
  }

  return Object.entries(sections)
    .filter(([_, todos]) => todos.length > 0) // 빈 섹션 제외
    .map(([sectionType, todos]) => ({
      key: sectionType,
      title: sectionTitles[sectionType as SectionTypeValue],
      type: sectionType as SectionTypeValue,
      count: todos.length,
      todos: sortType ? sortTodos(todos, sortType, true, locale) : todos,
    }))
}
